import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import AcademiaSelect from '../../common/AcademiaSelect';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import AdminPageShell from '../AdminPageShell';
import AdminToast from '../AdminToast';
import { AcademicPanelCard } from './AcademicPanelCard';

const tarifInputClass =
  'tarif-field__input w-full min-w-0 rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm tabular-nums text-slate-900 shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

function CurrencyField({ label, hint, className, children }) {
  return (
    <label className={className ? `tarif-field ${className}` : 'tarif-field'}>
      <span className="tarif-field__label">{label}</span>
      {children}
      {hint ? <span className="mt-1 text-xs text-on-surface-variant">{hint}</span> : null}
    </label>
  );
}

function formatRate(value, options = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('fr-FR', options);
}

export default function AcademiaCurrencyPanel({
  settings,
  isLoading,
  isError,
  isSaving,
  toastMessage,
  toastVariant,
  onDismissToast,
  onRefresh,
  onSaveCurrency,
}) {
  useRegisterPortalRefresh(onRefresh);
  const [form, setForm] = useState(null);
  const [recalculateTarifs, setRecalculateTarifs] = useState(false);

  useEffect(() => {
    if (settings?.currency) {
      setForm(settings.currency);
    }
  }, [settings]);

  const sideCard = useMemo(() => {
    const rate = form?.usdToCdfRate;
    const formatted =
      rate != null && Number.isFinite(Number(rate))
        ? `${Number(rate).toLocaleString('fr-FR')} CDF`
        : '—';
    return {
      label: 'Taux USD → CDF',
      value: isLoading || !form ? '…' : formatted,
      helper: form ? 'Devise principale : CDF (facturation)' : null,
      sideLinks: [
        { label: 'Tarifs', to: '/admin/tarifs', icon: 'payments' },
        { label: 'Tableau de bord', to: '/admin/dashboard', icon: 'dashboard' },
      ],
    };
  }, [form, isLoading]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!form) return;
    void onSaveCurrency({
      displayMode: form.displayMode,
      usdToCdfRate: Number(form.usdToCdfRate),
      cdfToUsdRate: Number(form.cdfToUsdRate),
      recalculateTarifs,
    });
  };

  const lastUpdateLabel = form?.updatedAt
    ? new Date(form.updatedAt).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';

  const displayModeLabel =
    form?.displayMode === 'DOUBLE_AFFICHAGE' ? 'Double affichage' : 'Devise principale (CDF)';

  return (
    <>
      <AdminToast message={toastMessage} variant={toastVariant} onClose={onDismissToast} />
      <AdminPageShell
        title="Devise et taux de change"
        description="La plateforme facture en CDF. Le taux USD sert à recalculer les tarifs de référence."
        sideCard={sideCard}
        onRetry={onRefresh}
        isLoading={isLoading || !form}
        isError={isError}
        loadingMessage="Chargement des paramètres…"
        errorMessage="Impossible de charger la configuration."
      >
        {form ? (
          <form onSubmit={handleSubmit} className="tarif-page-stack">
            <div className="tarif-info-banner">
              <span className="tarif-info-banner__icon" aria-hidden>
                <LucideSymbolIcon name="info" tone="inherit" size={20} />
              </span>
              <p>
                Tous les montants facturés sont en <strong>franc congolais (CDF)</strong>. Les références USD
                permettent de mettre à jour les tarifs lorsque le taux change — consultez aussi la page{' '}
                <Link to="/admin/tarifs" className="font-bold text-primary hover:underline">
                  Tarification
                </Link>
                .
              </p>
            </div>

            <AcademicPanelCard
              icon={<LucideSymbolIcon name="payments" tone="inherit" size={22} />}
              title="Configuration CDF"
              subtitle={`Dernière mise à jour : ${lastUpdateLabel}`}
              variant="accent"
              badge={<span className="academic-panel-badge">{displayModeLabel}</span>}
              footer={
                <button
                  type="submit"
                  disabled={isSaving}
                  className="academic-panel-btn academic-panel-btn--primary"
                >
                  <LucideSymbolIcon name="save" tone="inherit" size={18} className="text-white" />
                  {isSaving ? 'Enregistrement…' : 'Enregistrer la configuration'}
                </button>
              }
            >
              <div className="currency-rate-chips">
                <div className="currency-rate-chip">
                  <p className="currency-rate-chip__label">Taux USD → CDF</p>
                  <p className="currency-rate-chip__value">
                    {formatRate(form.usdToCdfRate)} <span className="text-sm font-semibold">CDF</span>
                  </p>
                </div>
                <div className="currency-rate-chip currency-rate-chip--muted">
                  <p className="currency-rate-chip__label">Taux CDF → USD</p>
                  <p className="currency-rate-chip__value">
                    {formatRate(form.cdfToUsdRate, { maximumFractionDigits: 6 })}
                  </p>
                </div>
              </div>

              <p className="tarif-subsection-title">Paramètres</p>

              <div className="mb-4">
                <span className="tarif-field__label">Devise de facturation</span>
                <div className="mt-1">
                  <span className="currency-devise-badge">
                    <LucideSymbolIcon name="payments" tone="inherit" size={18} />
                    CDF — Franc congolais
                  </span>
                </div>
              </div>

              <div className="tarif-field-grid">
                <CurrencyField label="Mode d'affichage">
                  <AcademiaSelect
                    fieldSize="sm"
                    value={form.displayMode}
                    onChange={(e) => setForm((c) => ({ ...c, displayMode: e.target.value }))}
                  >
                    <option value="DEVISE_PRINCIPALE">Devise principale (CDF)</option>
                    <option value="DOUBLE_AFFICHAGE">Double affichage</option>
                  </AcademiaSelect>
                </CurrencyField>

                <CurrencyField
                  label="Taux USD → CDF"
                  hint="Nombre de francs congolais pour 1 dollar américain."
                >
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className={tarifInputClass}
                    value={form.usdToCdfRate}
                    onChange={(e) => setForm((c) => ({ ...c, usdToCdfRate: e.target.value }))}
                  />
                </CurrencyField>

                <CurrencyField
                  label="Taux CDF → USD"
                  hint="Taux inverse (souvent une petite décimale)."
                  className="tarif-field--span-2"
                >
                  <input
                    type="number"
                    min={0}
                    step="0.000001"
                    className={tarifInputClass}
                    value={form.cdfToUsdRate}
                    onChange={(e) => setForm((c) => ({ ...c, cdfToUsdRate: e.target.value }))}
                  />
                </CurrencyField>
              </div>

              <label className="currency-option-panel">
                <input
                  type="checkbox"
                  checked={recalculateTarifs}
                  onChange={(e) => setRecalculateTarifs(e.target.checked)}
                />
                <span className="currency-option-panel__text">
                  <span className="currency-option-panel__title">
                    Recalculer les tarifs actifs à partir des références USD
                  </span>
                  <span className="currency-option-panel__hint">
                    Applique le nouveau taux aux lignes tarifaires qui ont une référence USD enregistrée.
                  </span>
                </span>
              </label>
            </AcademicPanelCard>
          </form>
        ) : null}
      </AdminPageShell>
    </>
  );
}
